import * as React from 'react'
import { supportedExtensions } from '../../utils';
import { useModalStore } from '../../store/use-modal-store';
import { useChatManagerStore } from '../../store/use-chat-manager-store';
import { useIngestionTrackingStore } from '../../store/use-ingestion-tracking-store';
import { useSmokeballSdkStore } from '../../store/use-smokeball-sdk-store';
import { useAccountMatterStore } from '../../store/use-account-matter-store';
import { usePromptTemplatesStore } from '@/lib/store/use-prompt-templates-store';


export function usePromptTemplateSelectFiles() {
  const sdk = useSmokeballSdkStore((store) => store.sdk)
  const showFileWarningModal = useModalStore((store) => store.showFileWarningModal)
  const setFileIds = useChatManagerStore((store) => store.setFileIds);
  const matterIngestionStatus = useIngestionTrackingStore((store) => store.matterIngestionStatus)
  const setPromptTemplateSelected = usePromptTemplatesStore((store) => store.setPromptTemplateSelected);

  const handleSelectFiles = React.useCallback((files: { id: string; fileExtension?: string | null }[]) => {
    if (!files.length) {
      setPromptTemplateSelected(null)
      return
    }
    const unsupportedFile = files.find(file => !supportedExtensions.includes(file.fileExtension || ''))
    if (unsupportedFile) {
      showFileWarningModal(`Archie cannot answer questions on ${unsupportedFile.fileExtension} files. Please choose another file to proceed.`)
      setPromptTemplateSelected(null)
      return
    }
    const currentFileIds = useChatManagerStore.getState().fileIds
    const uniqueFileIds = [
      ...new Set([
        ...files.map(file => file.id),
        ...(currentFileIds || [])
      ])
    ]
    setFileIds(uniqueFileIds)
    if (!uniqueFileIds.length) {
      setPromptTemplateSelected(null)
    }
  }, [setFileIds, setPromptTemplateSelected, showFileWarningModal])

  const handleOpenSelectFilesModal = React.useCallback(async () => {
    const matterStore = useAccountMatterStore.getState()
    const ingestionStore = useIngestionTrackingStore.getState()
    const tooManyFilesError = ['ERRPRE', 'ERRPOST'].includes(ingestionStore.matterIngestionStatus?.matterTrackingStatusCode || '')

    if (tooManyFilesError) {
      showFileWarningModal('Due to the volume of files on this matter, Archie cannot access this feature.')
      return
    }

    if (!!sdk) {
      const files = await sdk.files.select({
        matterId: matterStore.matterId,
        confirmationText: 'SELECT',
        fileSelectionLimit: 999,
        showEmails: true,
        title: 'Select Files To Use with Archie',
        heading: 'Select files to use with Archie',
        allowCombiningAsPdf: false,
        allowConversionToPdf: false
      })
      handleSelectFiles(files.map(({ fileId, fileExtension }) => ({ fileExtension, id: fileId })))
    }
  }, [handleSelectFiles, showFileWarningModal, sdk])

  return {
    handleSelectFiles,
    handleOpenSelectFilesModal,
  }
}
